import React from "react";
import { isValid } from "date-fns";
import { Icon } from "../core/Icon.jsx";
import { CalendarPanel } from "./CalendarPanel.jsx";
import {
  DEFAULT_DATE_FORMAT,
  formatDateValue,
  isCompleteDateInput,
  parseDateInput,
  resolveDateBounds,
  validateDate,
} from "./datePickerUtils.js";

/* DatePicker — labelled field + calendar popover (react-day-picker + date-fns).
   Replaces @material-ui/pickers DesktopDatePicker (REG-378 T017, FR-002).
   Display format defaults to dd/MM/yyyy; onChange receives Date | null. */

let _id = 0;

export function DatePicker({
  label,
  value = null,
  onChange,
  min,
  max,
  minDate,
  maxDate,
  disablePast = false,
  disableFuture = false,
  disabled = false,
  error,
  helper,
  format = DEFAULT_DATE_FORMAT,
  placeholder = "DD/MM/YYYY",
  required = false,
  size = "lg",
  disableHighlightToday = false,
  onError,
  onOpen,
  style,
  inputProps,
  id: idProp,
  ...rest
}) {
  const [open, setOpen] = React.useState(false);
  const [text, setText] = React.useState(() => formatDateValue(value, format));
  const [month, setMonth] = React.useState(() => (value && isValid(value) ? value : new Date()));
  const [focus, setFocus] = React.useState(false);
  const rootRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const id = React.useMemo(() => idProp || `tk-dp-${++_id}`, [idProp]);
  const height = size === "sm" ? 36 : size === "md" ? 44 : 48;

  const bounds = React.useMemo(
    () => resolveDateBounds({ min, max, minDate, maxDate, disablePast, disableFuture }),
    [min, max, minDate, maxDate, disablePast, disableFuture]
  );

  const valueMs = value && isValid(value) ? value.getTime() : null;
  const lastCommittedRef = React.useRef(value && isValid(value) ? value : null);

  React.useEffect(() => {
    if (value && isValid(value)) {
      lastCommittedRef.current = value;
    }
  }, [valueMs, value]);

  const emitError = (code) => {
    if (onError) onError(code);
  };

  const restoreLastCommitted = () => {
    const fallback = lastCommittedRef.current;
    if (!(fallback && isValid(fallback))) return false;
    setText(formatDateValue(fallback, format));
    if (onChange && (!value || !isValid(value) || value.getTime() !== fallback.getTime())) {
      onChange(fallback);
    }
    emitError(null);
    return true;
  };

  const clearTextWhenUncommitted = () => {
    if (value == null) setText("");
  };

  React.useEffect(() => {
    if (focus) return;
    if (value && isValid(value)) {
      setText(formatDateValue(value, format));
      setMonth(value);
      return;
    }
    if (value == null) {
      setText("");
      lastCommittedRef.current = null;
    }
    // Skip while focused. Leave text/ref alone for invalid Date placeholders so blur races
    // and officeholder DOB editing can still restore the last committed date.
  }, [valueMs, format, focus, value]);

  React.useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const commitDate = (date) => {
    const code = date ? validateDate(date, bounds) : null;
    emitError(code);
    if (code) return;
    if (onChange) onChange(date);
    setText(formatDateValue(date, format));
  };

  const handleInputChange = (e) => {
    const next = e.target.value;
    setText(next);
    if (!next.trim()) {
      emitError(null);
      if (onChange) onChange(null);
      return;
    }
    const parsed = parseDateInput(next, format);
    if (parsed === undefined) {
      emitError(isCompleteDateInput(next, format) ? "invalidDate" : null);
      return;
    }
    commitDate(parsed);
  };

  const handleInputBlur = () => {
    if (!text.trim()) {
      lastCommittedRef.current = null;
      emitError(null);
      if (onChange) onChange(null);
      setFocus(false);
      return;
    }
    const parsed = parseDateInput(text, format);
    if (parsed === undefined) {
      emitError("invalidDate");
      if (!restoreLastCommitted()) clearTextWhenUncommitted();
      setFocus(false);
      return;
    }
    const code = parsed ? validateDate(parsed, bounds) : null;
    emitError(code);
    if (code) {
      if (!restoreLastCommitted()) clearTextWhenUncommitted();
      setFocus(false);
      return;
    }
    commitDate(parsed);
    setFocus(false);
  };

  const handleDaySelect = (date) => {
    commitDate(date ?? null);
    setOpen(false);
    inputRef.current?.focus();
  };

  const openCalendar = () => {
    if (disabled) return;
    if (onOpen) onOpen();
    if (value && isValid(value)) setMonth(value);
    setOpen(true);
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") setOpen(false);
    if (e.key === "ArrowDown" && !open) {
      e.preventDefault();
      openCalendar();
    }
  };

  let border = "inset 0 0 0 1px var(--border)";
  if (error) border = "inset 0 0 0 1.5px var(--red)";
  else if (focus || open) border = "inset 0 0 0 1.5px var(--green-bright)";

  return (
    <div ref={rootRef} style={{ display: "flex", flexDirection: "column", gap: 6, position: "relative", ...style }} {...rest}>
      {label ? (
        <label htmlFor={id} style={{ fontFamily: "var(--font-sans)", fontWeight: 600, fontSize: 14, color: "var(--text-body)" }}>
          {label}
          {required ? <span style={{ color: "var(--red)" }}> *</span> : null}
        </label>
      ) : null}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          height,
          padding: "0 4px 0 14px",
          borderRadius: "var(--radius-xs)",
          background: disabled ? "var(--grey-bg)" : "var(--white)",
          boxShadow: disabled ? "inset 0 0 0 1px var(--border)" : border,
          transition: "box-shadow var(--dur) var(--ease)",
        }}
      >
        <input
          ref={inputRef}
          id={id}
          type="text"
          inputMode="numeric"
          value={text}
          onChange={handleInputChange}
          onFocus={() => setFocus(true)}
          onBlur={handleInputBlur}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={open}
          autoComplete="off"
          style={{
            flex: 1,
            minWidth: 0,
            border: "none",
            outline: "none",
            boxShadow: "none",
            background: "transparent",
            fontFamily: "var(--font-sans)",
            fontWeight: 500,
            fontSize: 14,
            color: disabled ? "var(--text-muted)" : "var(--text-body)",
            cursor: disabled ? "not-allowed" : "text",
          }}
          {...inputProps}
        />
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          aria-label="Open calendar"
          onClick={openCalendar}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 36,
            height: 36,
            border: "none",
            outline: "none",
            borderRadius: "var(--radius-xs)",
            background: "transparent",
            cursor: disabled ? "not-allowed" : "pointer",
            color: "var(--text-muted)",
          }}
        >
          <Icon name="calendar" size={18} color="var(--text-muted)" />
        </button>
      </div>

      {open ? (
        <div
          role="dialog"
          aria-label={label ? `${label} calendar` : "Choose date"}
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            zIndex: 40,
            background: "var(--surface-card)",
            color: "var(--text-body)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            boxShadow: "var(--shadow-inset-border), var(--shadow-3)",
            padding: 12,
          }}
        >
          <CalendarPanel
            mode="single"
            selected={value && isValid(value) ? value : undefined}
            onSelect={handleDaySelect}
            month={month}
            onMonthChange={setMonth}
            fromDate={bounds.fromDate ?? undefined}
            toDate={bounds.toDate ?? undefined}
            disableHighlightToday={disableHighlightToday}
            initialFocus
          />
        </div>
      ) : null}

      {error || helper ? (
        <div style={{ fontFamily: "var(--font-sans)", fontWeight: 500, fontSize: 12, color: error ? "var(--red)" : "var(--text-muted)" }}>
          {error || helper}
        </div>
      ) : null}
    </div>
  );
}

export default DatePicker;
